import React, { useEffect, useRef, useState } from 'react';
import { AlertTriangle, Focus, LoaderCircle, Move3D, Rotate3D, ZoomIn, ZoomOut } from 'lucide-react';
import { apiClient } from '@/lib/api-client';
import { Button } from '@/shared/ui/button';
import type { SkyMapNode } from './FlightPathPanel';
import { colmapToViewer, type SkyMapVec3 } from './skyMapCoordinates';

export { colmapToViewer } from './skyMapCoordinates';

interface SceneManifest {
  kind: 'ironview.sky-map.scene';
  renderer: 'point-cloud' | 'gaussian-splat';
  coordinateSystem: {
    rawToNormalized: { center: SkyMapVec3; scale: number };
  };
  splat?: {
    format: 'spz';
    gaussianCount: number;
    byteLength: number;
  };
  pointCloud: {
    pointCount?: number;
    strideBytes?: number;
    bounds: { min: [number, number, number]; max: [number, number, number] };
  };
}

interface Props {
  videoId: number;
  nodes: SkyMapNode[];
  selectedNodeId: string | null;
  onSelectNode: (node: SkyMapNode) => void;
  fitDirection?: SkyMapVec3;
  pathPresentation?: 'overlay' | 'occluded';
}

interface CameraState {
  yaw: number;
  pitch: number;
  distance: number;
  target: [number, number, number];
}

interface RendererController {
  fit: () => void;
  zoom: (factor: number) => void;
  dispose: () => void;
}

type Vec3 = SkyMapVec3;
type Mat4 = Float32Array;

const subtract = (a: Vec3, b: Vec3): Vec3 => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
const add = (a: Vec3, b: Vec3): Vec3 => [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
const scale = (v: Vec3, amount: number): Vec3 => [v[0] * amount, v[1] * amount, v[2] * amount];
const length = (v: Vec3) => Math.hypot(v[0], v[1], v[2]);
const normalize = (v: Vec3): Vec3 => { const size = length(v) || 1; return scale(v, 1 / size); };
const cross = (a: Vec3, b: Vec3): Vec3 => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
const dot = (a: Vec3, b: Vec3) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];

const multiply = (a: Mat4, b: Mat4): Mat4 => {
  const result = new Float32Array(16);
  for (let column = 0; column < 4; column += 1) {
    for (let row = 0; row < 4; row += 1) {
      result[column * 4 + row] =
        a[row] * b[column * 4] + a[4 + row] * b[column * 4 + 1] +
        a[8 + row] * b[column * 4 + 2] + a[12 + row] * b[column * 4 + 3];
    }
  }
  return result;
};

const perspective = (fov: number, aspect: number, near: number, far: number): Mat4 => {
  const f = 1 / Math.tan(fov / 2);
  const range = 1 / (near - far);
  return new Float32Array([f / aspect, 0, 0, 0, 0, f, 0, 0, 0, 0, (far + near) * range, -1, 0, 0, 2 * far * near * range, 0]);
};

const lookAt = (eye: Vec3, target: Vec3, up: Vec3): Mat4 => {
  const z = normalize(subtract(eye, target));
  const x = normalize(cross(up, z));
  const y = cross(z, x);
  return new Float32Array([x[0], y[0], z[0], 0, x[1], y[1], z[1], 0, x[2], y[2], z[2], 0, -dot(x, eye), -dot(y, eye), -dot(z, eye), 1]);
};

const transformPoint = (matrix: Mat4, point: Vec3): [number, number, number, number] => {
  const [x, y, z] = point;
  return [
    matrix[0] * x + matrix[4] * y + matrix[8] * z + matrix[12],
    matrix[1] * x + matrix[5] * y + matrix[9] * z + matrix[13],
    matrix[2] * x + matrix[6] * y + matrix[10] * z + matrix[14],
    matrix[3] * x + matrix[7] * y + matrix[11] * z + matrix[15],
  ];
};

const compileShader = (gl: WebGL2RenderingContext, type: number, source: string) => {
  const shader = gl.createShader(type);
  if (!shader) throw new Error('Unable to allocate a WebGL shader');
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(shader) || 'WebGL shader compilation failed');
  return shader;
};

const createProgram = (gl: WebGL2RenderingContext) => {
  const vertex = compileShader(gl, gl.VERTEX_SHADER, `#version 300 es
    in vec3 a_position; in vec4 a_color; uniform mat4 u_viewProjection; uniform float u_pointSize;
    out vec4 v_color; void main() { vec3 p = vec3(a_position.x, -a_position.y, -a_position.z);
      gl_Position = u_viewProjection * vec4(p, 1.0); gl_PointSize = u_pointSize; v_color = a_color; }`);
  const fragment = compileShader(gl, gl.FRAGMENT_SHADER, `#version 300 es
    precision highp float; in vec4 v_color; out vec4 color; uniform bool u_round;
    void main() { if (u_round && distance(gl_PointCoord, vec2(.5)) > .5) discard; color = v_color; }`);
  const program = gl.createProgram();
  if (!program) throw new Error('Unable to allocate a WebGL program');
  gl.attachShader(program, vertex); gl.attachShader(program, fragment); gl.linkProgram(program);
  gl.deleteShader(vertex); gl.deleteShader(fragment);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(program) || 'WebGL program linking failed');
  return program;
};

const parsePointCloud = (buffer: ArrayBuffer, expectedCount: number) => {
  const view = new DataView(buffer);
  if (buffer.byteLength < 12 || String.fromCharCode(...new Uint8Array(buffer, 0, 4)) !== 'IVPC') throw new Error('Invalid Sky Map point-cloud header');
  if (view.getUint32(4, true) !== 1) throw new Error('Unsupported Sky Map point-cloud version');
  const count = view.getUint32(8, true);
  if (count !== expectedCount || buffer.byteLength !== 12 + count * 16) throw new Error('Sky Map point-cloud payload is incomplete');
  return { count, data: new Uint8Array(buffer, 12) };
};

const createRenderer = (
  canvas: HTMLCanvasElement,
  pointData: Uint8Array,
  pointCount: number,
  bounds: SceneManifest['pointCloud']['bounds'],
  nodes: SkyMapNode[],
  selectedNodeId: () => string | null,
  onSelectNode: (node: SkyMapNode) => void,
): RendererController => {
  const gl = canvas.getContext('webgl2', { antialias: true, alpha: false });
  if (!gl) throw new Error('WebGL 2 is required for Sky Map');
  const program = createProgram(gl);
  const positionLocation = gl.getAttribLocation(program, 'a_position');
  const colorLocation = gl.getAttribLocation(program, 'a_color');
  const matrixLocation = gl.getUniformLocation(program, 'u_viewProjection');
  const sizeLocation = gl.getUniformLocation(program, 'u_pointSize');
  const roundLocation = gl.getUniformLocation(program, 'u_round');
  const resources: Array<WebGLBuffer | WebGLVertexArrayObject | WebGLProgram> = [program];

  const cloudVao = gl.createVertexArray(); const cloudBuffer = gl.createBuffer();
  if (!cloudVao || !cloudBuffer) throw new Error('Unable to allocate point-cloud buffers');
  resources.push(cloudVao, cloudBuffer); gl.bindVertexArray(cloudVao); gl.bindBuffer(gl.ARRAY_BUFFER, cloudBuffer);
  gl.bufferData(gl.ARRAY_BUFFER, pointData, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(positionLocation); gl.vertexAttribPointer(positionLocation, 3, gl.FLOAT, false, 16, 0);
  gl.enableVertexAttribArray(colorLocation); gl.vertexAttribPointer(colorLocation, 4, gl.UNSIGNED_BYTE, true, 16, 12);

  const pathPositions = new Float32Array(nodes.flatMap((node) => node.smoothedPosition));
  const pathVao = gl.createVertexArray(); const pathBuffer = gl.createBuffer();
  if (!pathVao || !pathBuffer) throw new Error('Unable to allocate flight-path buffers');
  resources.push(pathVao, pathBuffer); gl.bindVertexArray(pathVao); gl.bindBuffer(gl.ARRAY_BUFFER, pathBuffer);
  gl.bufferData(gl.ARRAY_BUFFER, pathPositions, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(positionLocation); gl.vertexAttribPointer(positionLocation, 3, gl.FLOAT, false, 12, 0);
  gl.disableVertexAttribArray(colorLocation);

  const convertedMin = colmapToViewer(bounds.min); const convertedMax = colmapToViewer(bounds.max);
  const sceneMin: Vec3 = convertedMin.map((value, axis) => Math.min(value, convertedMax[axis])) as Vec3;
  const sceneMax: Vec3 = convertedMin.map((value, axis) => Math.max(value, convertedMax[axis])) as Vec3;
  const center = scale(add(sceneMin, sceneMax), 0.5);
  const radius = Math.max(length(subtract(sceneMax, sceneMin)) * 0.5, 1);
  const camera: CameraState = { yaw: 0.72, pitch: 0.42, distance: radius * 2.2, target: [...center] };
  let viewProjection = new Float32Array(16);
  let pointerStart: { x: number; y: number; button: number; moved: boolean } | null = null;

  const fit = () => { camera.yaw = 0.72; camera.pitch = 0.42; camera.distance = radius * 2.2; camera.target = [...center]; render(); };
  const zoom = (factor: number) => { camera.distance = Math.max(radius * 0.08, Math.min(radius * 12, camera.distance * factor)); render(); };
  const eye = (): Vec3 => [
    camera.target[0] + camera.distance * Math.cos(camera.pitch) * Math.sin(camera.yaw),
    camera.target[1] + camera.distance * Math.sin(camera.pitch),
    camera.target[2] + camera.distance * Math.cos(camera.pitch) * Math.cos(camera.yaw),
  ];

  const render = () => {
    const ratio = Math.min(window.devicePixelRatio || 1, 2);
    const width = Math.max(1, Math.floor(canvas.clientWidth * ratio)); const height = Math.max(1, Math.floor(canvas.clientHeight * ratio));
    if (canvas.width !== width || canvas.height !== height) { canvas.width = width; canvas.height = height; }
    gl.viewport(0, 0, width, height); gl.clearColor(0.025, 0.04, 0.065, 1); gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
    gl.enable(gl.DEPTH_TEST); gl.enable(gl.BLEND); gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA); gl.useProgram(program);
    viewProjection = multiply(perspective(Math.PI / 4, width / height, Math.max(0.05, radius / 1000), radius * 30), lookAt(eye(), camera.target, [0, 1, 0]));
    gl.uniformMatrix4fv(matrixLocation, false, viewProjection);
    gl.bindVertexArray(cloudVao); gl.uniform1f(sizeLocation, Math.max(1.5, ratio * 1.35)); gl.uniform1i(roundLocation, 1); gl.drawArrays(gl.POINTS, 0, pointCount);
    gl.bindVertexArray(pathVao); gl.vertexAttrib4f(colorLocation, 0.25, 0.68, 0.95, 0.28); gl.uniform1f(sizeLocation, 1); gl.uniform1i(roundLocation, 0); gl.lineWidth(4); gl.drawArrays(gl.LINE_STRIP, 0, nodes.length);
    gl.vertexAttrib4f(colorLocation, 0.38, 0.76, 1, 1); gl.lineWidth(1); gl.drawArrays(gl.LINE_STRIP, 0, nodes.length);
    gl.uniform1i(roundLocation, 1); gl.uniform1f(sizeLocation, 14 * ratio); gl.vertexAttrib4f(colorLocation, 0.05, 0.11, 0.18, 1); gl.drawArrays(gl.POINTS, 0, nodes.length);
    gl.uniform1f(sizeLocation, 9 * ratio); gl.vertexAttrib4f(colorLocation, 0.38, 0.76, 1, 1); gl.drawArrays(gl.POINTS, 0, nodes.length);
    const selectedIndex = nodes.findIndex((node) => node.id === selectedNodeId());
    if (selectedIndex >= 0) { gl.uniform1f(sizeLocation, 22 * ratio); gl.vertexAttrib4f(colorLocation, 0.85, 0.94, 1, 1); gl.drawArrays(gl.POINTS, selectedIndex, 1); }
    gl.bindVertexArray(null);
  };

  const pick = (clientX: number, clientY: number) => {
    const rect = canvas.getBoundingClientRect(); let bestIndex = -1; let bestDistance = 28;
    nodes.forEach((node, index) => {
      const clip = transformPoint(viewProjection, colmapToViewer(node.smoothedPosition));
      if (clip[3] <= 0) return;
      const x = rect.left + (clip[0] / clip[3] * 0.5 + 0.5) * rect.width;
      const y = rect.top + (-clip[1] / clip[3] * 0.5 + 0.5) * rect.height;
      const distance = Math.hypot(clientX - x, clientY - y);
      if (distance < bestDistance) { bestDistance = distance; bestIndex = index; }
    });
    if (bestIndex >= 0) { onSelectNode(nodes[bestIndex]); render(); }
  };

  const pointerDown = (event: PointerEvent) => { canvas.setPointerCapture(event.pointerId); pointerStart = { x: event.clientX, y: event.clientY, button: event.button, moved: false }; };
  const pointerMove = (event: PointerEvent) => {
    if (!pointerStart) return; const dx = event.clientX - pointerStart.x; const dy = event.clientY - pointerStart.y;
    if (Math.hypot(dx, dy) > 3) pointerStart.moved = true;
    const pan = pointerStart.button === 1 || pointerStart.button === 2 || event.shiftKey;
    if (pan) {
      const forward = normalize(subtract(camera.target, eye())); const right = normalize(cross(forward, [0, 1, 0])); const up = normalize(cross(right, forward));
      camera.target = add(camera.target, add(scale(right, -dx * camera.distance * 0.0015), scale(up, dy * camera.distance * 0.0015)));
    } else { camera.yaw -= dx * 0.008; camera.pitch = Math.max(-1.45, Math.min(1.45, camera.pitch + dy * 0.008)); }
    pointerStart.x = event.clientX; pointerStart.y = event.clientY; render();
  };
  const pointerUp = (event: PointerEvent) => { if (pointerStart && !pointerStart.moved && pointerStart.button === 0) pick(event.clientX, event.clientY); pointerStart = null; };
  const wheel = (event: WheelEvent) => { event.preventDefault(); zoom(Math.exp(event.deltaY * 0.001)); };
  const contextMenu = (event: Event) => event.preventDefault();
  const resize = new ResizeObserver(render);
  canvas.addEventListener('pointerdown', pointerDown); canvas.addEventListener('pointermove', pointerMove); canvas.addEventListener('pointerup', pointerUp);
  canvas.addEventListener('pointercancel', pointerUp); canvas.addEventListener('wheel', wheel, { passive: false }); canvas.addEventListener('contextmenu', contextMenu); resize.observe(canvas); fit();

  return { fit, zoom, dispose: () => {
    resize.disconnect(); canvas.removeEventListener('pointerdown', pointerDown); canvas.removeEventListener('pointermove', pointerMove); canvas.removeEventListener('pointerup', pointerUp);
    canvas.removeEventListener('pointercancel', pointerUp); canvas.removeEventListener('wheel', wheel); canvas.removeEventListener('contextmenu', contextMenu);
    resources.forEach((resource) => {
      if (resource === program) gl.deleteProgram(program);
      else if (resource === cloudBuffer || resource === pathBuffer) gl.deleteBuffer(resource as WebGLBuffer);
      else gl.deleteVertexArray(resource as WebGLVertexArrayObject);
    });
  } };
};

export const SkyMapScene: React.FC<Props> = ({
  videoId,
  nodes,
  selectedNodeId,
  onSelectNode,
  fitDirection,
  pathPresentation = 'overlay',
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rendererRef = useRef<RendererController | null>(null);
  const selectedRef = useRef(selectedNodeId);
  const selectRef = useRef(onSelectNode);
  const [state, setState] = useState<'loading' | 'ready' | 'empty' | 'error'>('loading');
  const [error, setError] = useState('');
  selectedRef.current = selectedNodeId; selectRef.current = onSelectNode;

  useEffect(() => {
    const controller = new AbortController(); let disposed = false;
    setState('loading'); setError(''); rendererRef.current?.dispose(); rendererRef.current = null;
    apiClient.get<SceneManifest>(`/drones/${videoId}/flight-path/scene/`, { signal: controller.signal }).then(async (sceneResponse) => {
      if (disposed || !canvasRef.current) return;
      const scene = sceneResponse.data;
      if (scene.kind !== 'ironview.sky-map.scene') throw new Error('Unsupported Sky Map scene');

      if (scene.renderer === 'gaussian-splat' && scene.splat) {
        try {
          const { createGaussianRenderer } = await import('./SkyMapGaussianRenderer');
          const splatResponse = await apiClient.get<ArrayBuffer>(`/drones/${videoId}/flight-path/scene/splat/`, { responseType: 'arraybuffer', signal: controller.signal });
          if (disposed || !canvasRef.current) return;
          rendererRef.current = await createGaussianRenderer(
            canvasRef.current,
            splatResponse.data,
            scene,
            nodes,
            () => selectedRef.current,
            (node) => selectRef.current(node),
            { fitDirection, pathPresentation },
          );
          if (disposed) { rendererRef.current.dispose(); rendererRef.current = null; return; }
          setState('ready');
          return;
        } catch (reason) {
          if (controller.signal.aborted) return;
          console.warn('Gaussian Sky Map unavailable; using point-cloud fallback.', reason);
        }
      }

      const pointCount = scene.pointCloud.pointCount ?? 0;
      if (!pointCount) { setState('empty'); return; }
      const pointsResponse = await apiClient.get<ArrayBuffer>(`/drones/${videoId}/flight-path/scene/points/`, { responseType: 'arraybuffer', signal: controller.signal });
      if (disposed || !canvasRef.current) return;
      const points = parsePointCloud(pointsResponse.data, pointCount);
      rendererRef.current = createRenderer(canvasRef.current, points.data, points.count, scene.pointCloud.bounds, nodes, () => selectedRef.current, (node) => selectRef.current(node));
      setState('ready');
    }).catch((reason: unknown) => {
      if (controller.signal.aborted) return;
      setError(reason instanceof Error ? reason.message : 'The reconstructed scene could not be loaded.'); setState('error');
    });
    return () => { disposed = true; controller.abort(); rendererRef.current?.dispose(); rendererRef.current = null; };
  }, [fitDirection, nodes, pathPresentation, videoId]);

  useEffect(() => { if (state === 'ready') rendererRef.current?.zoom(1); }, [selectedNodeId, state]);

  return (
    <div className="relative min-h-[24rem] flex-1 overflow-hidden rounded-xl border border-border bg-[#07101a]">
      <canvas ref={canvasRef} className="absolute inset-0 size-full touch-none" aria-label="Interactive reconstructed Sky Map scene" />
      {state === 'loading' && <SceneState icon={LoaderCircle} title="Loading reconstructed site" detail="Preparing the COLMAP scene and camera path…" spin />}
      {state === 'empty' && <SceneState icon={Move3D} title="No reconstruction points" detail="The flight path is available, but this scene contains no renderable site geometry." />}
      {state === 'error' && <SceneState icon={AlertTriangle} title="Scene unavailable" detail={error} />}
      {state === 'ready' && (
        <>
          <div className="absolute left-3 top-3 flex items-center gap-2 rounded-[11px] border border-border bg-background/80 p-1.5 shadow-slate backdrop-blur">
            <Control icon={ZoomIn} label="Zoom in" onClick={() => rendererRef.current?.zoom(0.8)} />
            <Control icon={ZoomOut} label="Zoom out" onClick={() => rendererRef.current?.zoom(1.25)} />
            <Control icon={Focus} label="Fit scene" onClick={() => rendererRef.current?.fit()} />
          </div>
          <div className="pointer-events-none absolute bottom-3 left-3 flex flex-wrap gap-2 text-[10px] font-semibold text-muted-foreground">
            <span className="rounded-lg border border-border bg-background/75 px-2 py-1 backdrop-blur"><Rotate3D className="mr-1 inline size-3 text-primary" />Drag to orbit</span>
            <span className="rounded-lg border border-border bg-background/75 px-2 py-1 backdrop-blur"><Move3D className="mr-1 inline size-3 text-primary" />Shift-drag to pan</span>
          </div>
        </>
      )}
    </div>
  );
};

const Control: React.FC<{ icon: React.ElementType; label: string; onClick: () => void }> = ({ icon: Icon, label, onClick }) => (
  <Button type="button" variant="ghost" size="icon" className="size-8 rounded-[9px] text-muted-foreground hover:bg-primary/12 hover:text-primary" aria-label={label} title={label} onClick={onClick}>
    <Icon className="size-4" strokeWidth={1.9} />
  </Button>
);

const SceneState: React.FC<{ icon: React.ElementType; title: string; detail: string; spin?: boolean }> = ({ icon: Icon, title, detail, spin = false }) => (
  <div className="absolute inset-0 grid place-items-center bg-background/85 p-8 text-center backdrop-blur-sm">
    <div className="max-w-xs">
      <Icon className={`mx-auto size-7 text-primary ${spin ? 'animate-spin' : ''}`} strokeWidth={1.7} />
      <h3 className="mt-3 font-display text-sm font-bold text-foreground">{title}</h3>
      <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{detail}</p>
    </div>
  </div>
);
